import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  WritableSignal,
} from "@angular/core";
import { HttpClient } from "@angular/common/http";

@Component({
  selector: "app-svg-icon",
  templateUrl: "./svg-icon.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: "./svg-icon.component.scss",
})
export class SvgIconComponent implements OnInit, OnChanges {
  public readonly icon: InputSignal<string> = input.required<string>();
  public readonly link: InputSignal<string | undefined> = input<string | undefined>();
  public readonly type: InputSignal<"primary" | "secondary" | "full"> = input<
    "primary" | "secondary" | "full"
  >("primary");
  public readonly blank: InputSignal<boolean> = input<boolean>(false);
  public readonly svgSize: InputSignal<string | undefined> = input<string | undefined>();

  private static _instanceCount: number = 0;

  private readonly _http: HttpClient = inject(HttpClient);
  private readonly _el: ElementRef<HTMLElement> = inject(ElementRef);

  private readonly _imageSrc: WritableSignal<string> = signal<string>("");

  public ngOnInit(): void {
    this._loadSvg();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes["icon"] && !changes["icon"].firstChange) {
      this._loadSvg();
    }
  }

  private _loadSvg(): void {
    this._http.get(this.icon(), { responseType: "text" }).subscribe((svg: string) => {
      const uniqueSvg: string = this._makeIdsUnique(svg);
      if (this.blank()) {
        this._el.nativeElement.innerHTML = uniqueSvg;
      } else {
        (this._el.nativeElement.querySelector(".social-icon") as HTMLElement).innerHTML = uniqueSvg;
        this._setCustomSize();
      }
    });
  }

  // Rendering the same SVG asset more than once on a page would otherwise duplicate its
  // internal ids (defs/pattern/clipPath), and browsers resolve url(#id)/href="#id" references
  // to a single element document-wide — breaking whichever instance isn't picked, especially
  // when a duplicate sibling is hidden via display:none.
  private _makeIdsUnique(svg: string): string {
    const suffix: string = `-i${SvgIconComponent._instanceCount++}`;
    const ids: Set<string> = new Set<string>();
    const idPattern: RegExp = /\sid="([^"]+)"/g;
    let match: RegExpExecArray | null;
    while ((match = idPattern.exec(svg))) {
      ids.add(match[1]);
    }

    let result: string = svg;
    ids.forEach((id: string) => {
      const escapedId: string = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      result = result
        .replace(new RegExp(`id="${escapedId}"`, "g"), `id="${id}${suffix}"`)
        .replace(new RegExp(`(url\\(#|href="#)${escapedId}(\\)|")`, "g"), `$1${id}${suffix}$2`);
    });
    return result;
  }

  private _setCustomSize(): void {
    const svgSize: string | undefined = this.svgSize();
    if (svgSize) {
      const element: SVGSVGElement = this._el.nativeElement.querySelector("svg") as SVGSVGElement;
      element.style.width = svgSize;
      element.style.height = svgSize;
    }
  }
}
